/**
 * Which pages point at which.
 *
 * Twenty new pages reachable only from the nav and the grid are twenty
 * orphans. Internal links are how a crawler discovers depth, how PageRank
 * reaches a new page, and how an answer engine works out that dental charting
 * and treatment plans belong to the same subject. Every entry below is a real
 * relationship — a laser clinic genuinely needs treatment areas — not a link
 * farm; a page that has nothing to point at gets no block.
 *
 * Keys are feature keys and category keys. Route slugs differ from category
 * keys (/for/laser-hair-removal renders `laser`), so verticals are addressed
 * by key here and resolved through VERTICAL_ROUTE.
 */

/** Feature -> the other features a reader of it usually needs next. */
export const RELATED_FEATURES = {
  'dental-charting': ['records', 'treatment-plans', 'agreements'],
  'treatment-areas': ['injectables', 'records', 'treatment-plans'],
  'injectables': ['treatment-areas', 'agreements', 'records'],
  'records': ['agreements', 'treatment-plans', 'dental-charting'],
  'treatment-plans': ['records', 'treatment-areas', 'payments'],
  'agreements': ['records', 'injectables', 'treatment-plans'],
  'courses': ['rooms', 'payments', 'messages'],
  'shop': ['pos', 'products', 'finance'],
  'pos': ['shop', 'payments', 'finance'],
  'auto-ads': ['marketing', 'reviews', 'website'],
  'hospitality': ['rooms', 'payments', 'messages'],
  'rooms': ['group-booking', 'appointments', 'courses'],
  'group-booking': ['rooms', 'appointments', 'payments'],
  'messages': ['notifications', 'reminders', 'crm'],

  // ── Pages that pre-date the 2026-09 expansion ─────────────────────────────
  // Extended so the whole site clusters, not only the new pages: 102 of 177
  // feature and vertical pages had no outbound internal links at all.
  'booking': ['appointments', 'calendar', 'website'],
  'appointments': ['calendar', 'group-booking', 'rooms'],
  'crm': ['records', 'messages', 'reminders'],
  'notifications': ['messages', 'reminders', 'crm'],
  'website': ['profile', 'gallery', 'reviews'],
  'profile': ['website', 'gallery', 'qr'],
  'zoom': ['courses', 'appointments', 'website'],
  'reminders': ['notifications', 'messages', 'crm'],
  'team': ['roles', 'rooms', 'appointments'],
  'discounts': ['marketing', 'crm', 'payments'],
  'payments': ['pos', 'finance', 'group-booking'],
  'gallery': ['website', 'reviews', 'profile'],
  'services': ['treatment-areas', 'appointments', 'payments'],
  'calendar': ['appointments', 'rooms', 'team'],
  'roles': ['team', 'records', 'finance'],
  'qr': ['website', 'profile', 'marketing'],
  'app': ['website', 'appointments', 'messages'],
  'finance': ['payments', 'pos', 'shop'],
  'reviews': ['website', 'marketing', 'gallery'],
  'products': ['shop', 'pos', 'finance'],
  'marketing': ['auto-ads', 'discounts', 'reviews'],
};

/** Feature -> the verticals that lean on it, by CATEGORY KEY. */
export const FEATURE_VERTICALS = {
  'dental-charting': ['dental'],
  'treatment-areas': ['laser', 'medspa'],
  'injectables': ['aesthetic', 'medspa'],
  'records': ['clinics', 'physio', 'nutrition'],
  'treatment-plans': ['physio', 'dental', 'clinics'],
  'agreements': ['aesthetic', 'laser', 'clinics'],
  'courses': ['academies'],
  'shop': ['beauty', 'hair'],
  'pos': ['barber', 'nails'],
  'auto-ads': ['hair', 'barber'],
  'hospitality': ['rentals'],
  'rooms': ['spa', 'massage'],
  'group-booking': ['spa', 'massage'],
  'messages': ['clinics', 'fitness'],

  // ── Pre-existing feature pages ────────────────────────────────────────────
  'booking': ['hair', 'barber', 'nails'],
  'appointments': ['clinics', 'spa'],
  'crm': ['hair', 'clinics'],
  'notifications': ['barber', 'nails'],
  'website': ['beauty', 'tattoo'],
  'profile': ['hair', 'beauty'],
  'zoom': ['online', 'nutrition'],
  'reminders': ['dental', 'clinics'],
  'team': ['spa', 'hair'],
  'discounts': ['nails', 'tanning'],
  'payments': ['medspa', 'spa'],
  'gallery': ['tattoo', 'beauty'],
  'services': ['massage', 'spa'],
  'calendar': ['barber', 'fitness'],
  'roles': ['clinics', 'spa'],
  'qr': ['barber', 'pet'],
  'app': ['pet', 'fitness'],
  'finance': ['medspa', 'hair'],
  'reviews': ['beauty', 'dental'],
  'products': ['hair', 'beauty'],
  'marketing': ['tanning', 'fitness'],
};

/** Vertical (category key) -> the features that page is really about. */
export const VERTICAL_FEATURES = {
  laser: ['treatment-areas', 'agreements', 'records'],
  aesthetic: ['injectables', 'agreements', 'records'],
  academies: ['courses', 'rooms', 'payments'],
  rentals: ['hospitality', 'payments', 'messages'],
  physio: ['records', 'treatment-plans', 'treatment-areas'],
  nutrition: ['records', 'treatment-plans', 'zoom'],
  dental: ['dental-charting', 'treatment-plans', 'agreements'],
  clinics: ['records', 'treatment-plans', 'agreements'],
  medspa: ['injectables', 'treatment-areas', 'agreements'],

  // ── Verticals that pre-date the expansion ─────────────────────────────────
  hair: ['booking', 'crm', 'shop'],
  barber: ['booking', 'pos', 'calendar'],
  nails: ['booking', 'discounts', 'pos'],
  spa: ['rooms', 'group-booking', 'payments'],
  massage: ['rooms', 'group-booking', 'services'],
  beauty: ['gallery', 'website', 'shop'],
  fitness: ['courses', 'team', 'payments'],
  tanning: ['rooms', 'discounts', 'payments'],
  tattoo: ['gallery', 'agreements', 'payments'],
  pet: ['crm', 'reminders', 'app'],
  online: ['zoom', 'payments', 'messages'],
  other: ['booking', 'crm', 'payments'],
};

/**
 * Feature key -> its route slug, where the two differ.
 *
 * Only one does: featureKey `booking` renders at /features/online-booking.
 * Linking to /features/booking would 404, which is what the link guard caught.
 */
export const FEATURE_ROUTE = {
  booking: 'online-booking',
};

/** The path for a feature key, honouring the one slug that differs. */
export function featurePath(key) {
  return `/features/${FEATURE_ROUTE[key] || key}`;
}

/** Category key -> its route slug. The two are deliberately different. */
export const VERTICAL_ROUTE = {
  hair: 'hair-salons', barber: 'barber-shops', nails: 'nail-salons', spa: 'spa',
  massage: 'massage', medspa: 'med-spa', beauty: 'beauty', fitness: 'fitness',
  tanning: 'tanning', tattoo: 'tattoo', pet: 'pet-grooming', dental: 'dental',
  clinics: 'clinics', online: 'online', other: 'other',
  laser: 'laser-hair-removal', aesthetic: 'aesthetic-clinics',
  academies: 'academies', rentals: 'vacation-rentals',
  physio: 'physiotherapy', nutrition: 'nutrition',
};
